import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import {
  ExportRefundProfileForm,
  profileResponseToForm,
} from '../exportRefund';

interface UserSummary {
  id: string;
  email: string;
  full_name?: string | null;
}

const AdminUserProfileDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<ExportRefundProfileForm | null>(null);
  const [userSummary, setUserSummary] = useState<UserSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [issueLast4, setIssueLast4] = useState('');
  const [issuingCard, setIssuingCard] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!userId) return;
      const token = localStorage.getItem('access_token');
      try {
        const [profileResponse, userResponse] = await Promise.all([
          axios.get(API.getExportRefundProfile(userId), {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(API.getUserProfile(userId), {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setProfile(profileResponseToForm(profileResponse.data));
        setUserSummary(userResponse.data);
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError('Failed to load user profile.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userId]);

  useEffect(() => {
    const loadPreviews = async () => {
      if (!profile || !userId) return;
      const token = localStorage.getItem('access_token');
      const filenames = [profile.id_photo_url, profile.selfie_photo_url, profile.proof_address_photo_url].filter(
        (filename): filename is string => Boolean(filename),
      );
      const entries = await Promise.all(
        filenames.map(async (filename) => {
          const response = await axios.get(API.downloadExportRefundProfilePhoto(userId, filename), {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          });
          return [filename, window.URL.createObjectURL(response.data)] as const;
        }),
      );
      setPhotoUrls(Object.fromEntries(entries));
    };

    loadPreviews();
  }, [profile, userId]);

  const setField = (field: keyof ExportRefundProfileForm, value: string | boolean) => {
    setProfile((current) => (current ? { ...current, [field]: value } : current));
  };

  const saveProfile = async () => {
    if (!profile || !userId) return;
    setSaving(true);
    setMessage('');
    setError('');
    const token = localStorage.getItem('access_token');
    try {
      const response = await axios.put(API.saveExportRefundProfile(userId), profile, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(profileResponseToForm(response.data));
      setMessage('Profile saved successfully.');
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError('Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (field: 'face_check_completed' | 'id_check_completed' | 'company_card_requested') => {
    if (!profile || !userId) return;
    setSaving(true);
    const token = localStorage.getItem('access_token');
    try {
      const updatedProfile = { ...profile, [field]: !profile[field] };
      const response = await axios.put(API.saveExportRefundProfile(userId), updatedProfile, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(profileResponseToForm(response.data));
      setMessage('Status updated successfully.');
    } catch (err) {
      console.error('Failed to update status:', err);
      setError('Failed to update status.');
    } finally {
      setSaving(false);
    }
  };

  const approveApplication = async () => {
    if (!userId) return;
    setSaving(true);
    setMessage('');
    setError('');
    const token = localStorage.getItem('access_token');
    try {
      const response = await axios.post(API.approveExportRefundProfile(userId), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(profileResponseToForm(response.data));
      setMessage('Application approved successfully.');
    } catch (err) {
      console.error('Failed to approve application:', err);
      setError('Failed to approve application.');
    } finally {
      setSaving(false);
    }
  };

  const rejectApplication = async () => {
    if (!userId) return;
    if (!window.confirm('Reject this card application? The £13 pre-authorised hold will be released back to the customer\'s card, and the application returns to draft for revision.')) return;
    setSaving(true);
    setMessage('');
    setError('');
    const token = localStorage.getItem('access_token');
    try {
      const response = await axios.post(API.rejectExportRefundProfile(userId), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(profileResponseToForm(response.data));
      setMessage('Application rejected — returned to draft for the customer to revise.');
    } catch (err) {
      console.error('Failed to reject application:', err);
      setError('Failed to reject application.');
    } finally {
      setSaving(false);
    }
  };

  const issueCard = async () => {
    if (!userId || !/^\d{4}$/.test(issueLast4)) return;
    setIssuingCard(true);
    setMessage('');
    setError('');
    const token = localStorage.getItem('access_token');
    try {
      const response = await axios.post(
        API.issueExportRefundCard(userId),
        { card_number_last4: issueLast4 },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProfile(profileResponseToForm(response.data));
      setIssueLast4('');
      setMessage('Card issued and activated.');
    } catch (err: any) {
      console.error('Failed to issue card:', err);
      setError(err.response?.data?.detail || 'Failed to issue card.');
    } finally {
      setIssuingCard(false);
    }
  };

  const deleteAccount = async () => {
    if (!userId) return;
    if (!window.confirm(`Permanently delete this account (${userSummary?.email || userId})? This cannot be undone.`)) return;
    setDeleting(true);
    setMessage('');
    setError('');
    const token = localStorage.getItem('access_token');
    try {
      await axios.delete(API.adminDeleteUser(userId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate('/admin/users');
    } catch (err: any) {
      console.error('Failed to delete account:', err);
      setError(err.response?.data?.detail || 'Failed to delete account.');
      setDeleting(false);
    }
  };

  if (!user || !user.is_admin) return <div>Access Denied</div>;

  return (
    <div className="dashboard-page">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div
          className="section-card"
          style={{ background: 'linear-gradient(135deg, #102542 0%, #1a3c68 62%, #c64d3b 100%)', color: '#fff', marginBottom: '24px' }}
        >
          <div className="page-toolbar">
            <div>
              <h1 className="responsive-h1">User Profile Management</h1>
              <p style={{ opacity: 0.9 }}>Review identity documents and approve submitted applications.</p>
            </div>
            <Link
              to="/admin/users"
              className="btn-primary"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', textDecoration: 'none' }}
            >
              Back to User List
            </Link>
          </div>
        </div>

        {(message || error) && (
          <div
            style={{
              marginBottom: '20px',
              padding: '14px 16px',
              borderRadius: '14px',
              background: error ? '#fff1ef' : '#edf8ee',
              color: error ? '#9f2d20' : '#1d5c30',
              border: `1px solid ${error ? '#f0c0b9' : '#bfe0c4'}`,
            }}
          >
            {error || message}
          </div>
        )}

        {loading ? (
          <div className="section-card">Loading profile...</div>
        ) : profile ? (
          <div style={{ display: 'grid', gap: '24px' }}>
            <section className="section-card">
              <div className="page-toolbar" style={{ marginBottom: '20px' }}>
                <h2 className="responsive-h2" style={{ color: '#102542', margin: 0 }}>Application Review</h2>
                <div className="status-pill">{(profile.application_status || 'draft').replace(/_/g, ' ')}</div>
              </div>

              <div className="responsive-card-grid">
                <div style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '12px', textAlign: 'center' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>ID / Passport</p>
                  {profile.id_photo_url ? (
                    photoUrls[profile.id_photo_url] ? (
                      <img src={photoUrls[profile.id_photo_url]} alt="ID" style={{ maxWidth: '100%', maxHeight: '300px' }} />
                    ) : (
                      <p style={{ color: '#666' }}>Loading image...</p>
                    )
                  ) : (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>No ID photo uploaded</p>
                  )}
                  <div style={{ marginTop: '10px' }}>
                    <button className={profile.id_check_completed ? 'btn-success' : 'btn-primary'} onClick={() => updateStatus('id_check_completed')} disabled={saving}>
                      {profile.id_check_completed ? 'Mark ID as Pending' : 'Approve ID Check'}
                    </button>
                  </div>
                </div>

                <div style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '12px', textAlign: 'center' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Selfie</p>
                  {profile.selfie_photo_url ? (
                    photoUrls[profile.selfie_photo_url] ? (
                      <img src={photoUrls[profile.selfie_photo_url]} alt="Selfie" style={{ maxWidth: '100%', maxHeight: '300px' }} />
                    ) : (
                      <p style={{ color: '#666' }}>Loading image...</p>
                    )
                  ) : (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>No selfie uploaded</p>
                  )}
                  <div style={{ marginTop: '10px' }}>
                    <button className={profile.face_check_completed ? 'btn-success' : 'btn-primary'} onClick={() => updateStatus('face_check_completed')} disabled={saving}>
                      {profile.face_check_completed ? 'Mark Face as Pending' : 'Approve Face Check'}
                    </button>
                  </div>
                </div>

                <div style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '12px', textAlign: 'center' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Proof of Address</p>
                  {profile.proof_address_photo_url ? (
                    photoUrls[profile.proof_address_photo_url] ? (
                      <img src={photoUrls[profile.proof_address_photo_url]} alt="Proof of address" style={{ maxWidth: '100%', maxHeight: '300px' }} />
                    ) : (
                      <p style={{ color: '#666' }}>Loading image...</p>
                    )
                  ) : (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>No proof of address uploaded</p>
                  )}
                </div>
              </div>

              <div style={{ marginTop: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  className={profile.application_status === 'approved' ? 'btn-success' : 'btn-primary'}
                  onClick={approveApplication}
                  disabled={saving || profile.application_status === 'approved'}
                  title="Approving captures the customer's £13 pre-authorised fee"
                >
                  {profile.application_status === 'approved' ? '✓ Review Complete — Approved (fee captured)' : 'Review Complete — Approve & Capture £13'}
                </button>
                {profile.application_status !== 'approved' && (
                  <button
                    className="btn-primary"
                    style={{ background: '#DC2626' }}
                    onClick={rejectApplication}
                    disabled={saving || profile.application_status === 'draft'}
                    title={profile.application_status === 'draft' ? 'Nothing to reject — application has not been submitted' : 'Rejecting releases the customer\'s £13 hold back to their card'}
                  >
                    Review Complete — Reject
                  </button>
                )}
              </div>
            </section>

            {profile.company_card_requested && (
              <section className="section-card">
                <div className="page-toolbar" style={{ marginBottom: '20px' }}>
                  <h2 className="responsive-h2" style={{ color: '#102542', margin: 0 }}>Partner Visa Card</h2>
                  <div className="status-pill">{profile.card_status.replace(/_/g, ' ').toUpperCase()}</div>
                </div>
                <div className="responsive-card-grid">
                  <div>
                    <label style={{ fontSize: '12px', color: '#888' }}>£13 Admin Fee</label>
                    <div style={{ fontSize: '16px', fontWeight: 500 }}>{profile.card_fee_paid ? 'Paid' : 'Unpaid'}</div>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#888' }}>Card Number</label>
                    <div style={{ fontSize: '16px', fontWeight: 500 }}>{profile.card_number_last4 ? `•••• ${profile.card_number_last4}` : 'Not issued'}</div>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#888' }}>Card Balance</label>
                    <div style={{ fontSize: '16px', fontWeight: 500 }}>£{profile.card_balance.toFixed(2)}</div>
                  </div>
                </div>
                {profile.card_status === 'frozen' || profile.card_status === 'violation' || profile.card_status === 'legal_escalation' ? (
                  <p style={{ marginTop: '16px', color: '#9f2d20' }}>
                    Frozen: {profile.risk_freeze_reason || 'Compliance violation'}
                  </p>
                ) : null}
                {profile.card_status === 'pending_issuance' && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
                      Approved and fee paid — not yet issued. Create the card on the virtual bank's own
                      dashboard, then enter its real last 4 digits here to activate it.
                    </p>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        className="form-input"
                        style={{ maxWidth: '140px' }}
                        placeholder="Last 4 digits"
                        maxLength={4}
                        value={issueLast4}
                        onChange={(e) => setIssueLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      />
                      <button
                        className="btn-primary"
                        onClick={issueCard}
                        disabled={issuingCard || !/^\d{4}$/.test(issueLast4)}
                      >
                        {issuingCard ? 'Issuing...' : 'Issue Card'}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}

            <section className="section-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 className="responsive-h2" style={{ color: '#102542', margin: 0 }}>User Record</h2>
                {user.id !== userId && (
                  <button
                    className="btn-primary"
                    style={{ background: '#DC2626' }}
                    onClick={deleteAccount}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Delete Account'}
                  </button>
                )}
              </div>
              <div className="form-grid">
                <div>
                  <label style={{ fontSize: '12px', color: '#888' }}>User ID</label>
                  <div style={{ fontSize: '16px', fontWeight: 500 }}>{userSummary?.id || userId || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888' }}>Account name</label>
                  <div style={{ fontSize: '16px', fontWeight: 500 }}>{userSummary?.full_name || 'N/A'}</div>
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888' }}>Account email</label>
                  <div style={{ fontSize: '16px', fontWeight: 500 }}>{userSummary?.email || 'N/A'}</div>
                </div>
              </div>

              <h2 className="responsive-h2" style={{ color: '#102542', marginTop: '30px', marginBottom: '20px' }}>Applicant Details</h2>
              <div className="form-grid">
                <div>
                  <label style={{ fontSize: '12px', color: '#888' }}>Legal full name</label>
                  <input className="form-input" value={profile.legal_full_name} onChange={(e) => setField('legal_full_name', e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888' }}>Birthday</label>
                  <input type="date" className="form-input" value={profile.date_of_birth} onChange={(e) => setField('date_of_birth', e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888' }}>Contact phone</label>
                  <input className="form-input" value={profile.contact_phone} onChange={(e) => setField('contact_phone', e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888' }}>WhatsApp ID</label>
                  <input className="form-input" value={profile.whatsapp_id} onChange={(e) => setField('whatsapp_id', e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888' }}>Registered address</label>
                  <textarea
                    className="form-input"
                    value={profile.registered_address}
                    onChange={(e) => setField('registered_address', e.target.value)}
                    style={{ minHeight: '88px', resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: '#888' }}>Contact address</label>
                  <textarea
                    className="form-input"
                    value={profile.contact_address}
                    onChange={(e) => setField('contact_address', e.target.value)}
                    style={{ minHeight: '88px', resize: 'vertical' }}
                  />
                </div>
              </div>

              <h3 style={{ color: '#102542', marginTop: '30px', marginBottom: '15px', fontSize: '18px' }}>Bank Details</h3>
              <div className="form-grid">
                <div><label style={{ fontSize: '12px', color: '#888' }}>Bank account name</label><input className="form-input" value={profile.bank_account_name} onChange={(e) => setField('bank_account_name', e.target.value)} /></div>
                <div><label style={{ fontSize: '12px', color: '#888' }}>Bank name</label><input className="form-input" value={profile.bank_name} onChange={(e) => setField('bank_name', e.target.value)} /></div>
                <div><label style={{ fontSize: '12px', color: '#888' }}>Account number</label><input className="form-input" value={profile.bank_account_number} onChange={(e) => setField('bank_account_number', e.target.value)} /></div>
                <div><label style={{ fontSize: '12px', color: '#888' }}>Sort Code</label><input className="form-input" value={profile.bank_sort_code} onChange={(e) => setField('bank_sort_code', e.target.value)} /></div>
                <div><label style={{ fontSize: '12px', color: '#888' }}>IBAN</label><input className="form-input" value={profile.iban} onChange={(e) => setField('iban', e.target.value)} /></div>
                <div><label style={{ fontSize: '12px', color: '#888' }}>SWIFT</label><input className="form-input" value={profile.swift_code} onChange={(e) => setField('swift_code', e.target.value)} /></div>
              </div>

              <h3 style={{ color: '#102542', marginTop: '30px', marginBottom: '15px', fontSize: '18px' }}>Verification Status</h3>
              <div className="status-row">
                <div className="status-pill">{profile.id_check_completed ? 'ID Check Approved' : 'ID Check Pending'}</div>
                <div className="status-pill">{profile.face_check_completed ? 'Face Check Approved' : 'Face Check Pending'}</div>
                <div className="status-pill">{profile.company_card_requested ? 'Debit Card Requested' : 'Debit Card Not Requested'}</div>
              </div>

              <div style={{ marginTop: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button className="btn-success" onClick={saveProfile} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Profile Changes'}
                </button>
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AdminUserProfileDetail;
